// Product gallery: native horizontal scrolling (keeps the phone's own swipe
// momentum and snap) with an infinite loop. A copy of the last slide sits before
// the first and a copy of the first after the last, so swiping past the last
// photo is a normal one-slide swipe onto the copy of photo 1; once it settles
// the scroller silently jumps to the real photo 1.
// Mobile Safari ignores scrollTo while momentum/snap is active, so the jump
// briefly turns off overflow, snap and smooth scrolling and sets scrollLeft.
// Share uses the native sheet when available, otherwise copies the product link.
const gallery = document.querySelector('[data-product-gallery]');
const nav = document.querySelector('[data-gallery-nav]');

if (gallery && nav) {
  const thumbs = [...nav.querySelectorAll('[data-gallery-thumb]')];
  const dots = [...document.querySelectorAll('[data-gallery-dot]')];
  const count = thumbs.length;
  const width = () => Math.max(gallery.clientWidth, 1);
  const looping = count > 1 && gallery.children.length === count;
  const offset = looping ? 1 : 0;

  const jumpTo = (pos) => {
    const style = gallery.style;
    style.overflowX = 'hidden';
    style.scrollSnapType = 'none';
    style.scrollBehavior = 'auto';
    gallery.scrollLeft = pos * width();
    void gallery.offsetWidth;
    requestAnimationFrame(() => {
      style.overflowX = '';
      style.scrollSnapType = '';
      style.scrollBehavior = '';
      // Re-assert in case restoring snap moved it.
      if (Math.abs(gallery.scrollLeft - pos * width()) > 2) gallery.scrollLeft = pos * width();
    });
  };

  if (looping) {
    const makeCopy = (slide) => {
      const copy = slide.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.inert = true;
      copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      copy.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
      return copy;
    };
    const firstCopy = makeCopy(gallery.firstElementChild);
    const lastCopy = makeCopy(gallery.lastElementChild);
    gallery.append(firstCopy);
    gallery.prepend(lastCopy);
    jumpTo(1);
  }

  const position = () => Math.round(gallery.scrollLeft / width());
  const current = () => (((position() - offset) % count) + count) % count;
  const goTo = (index) => {
    const target = looping
      ? Math.max(-1, Math.min(count, index))
      : Math.max(0, Math.min(count - 1, index));
    gallery.scrollTo({ left: (target + offset) * width(), behavior: 'smooth' });
  };

  if (looping) {
    let touching = false;
    let settleTimer = 0;
    const fixCopy = () => {
      const pos = position();
      if (pos <= 0) jumpTo(count);
      else if (pos >= count + 1) jumpTo(1);
    };
    const settle = () => {
      if (!touching) fixCopy();
    };
    const queueSettle = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 120);
    };
    // A new swipe that starts on a copy first moves to the real slide, so there
    // is always a next photo to swipe to.
    gallery.addEventListener('touchstart', () => { touching = true; fixCopy(); }, { passive: true });
    gallery.addEventListener('touchend', () => { touching = false; queueSettle(); }, { passive: true });
    gallery.addEventListener('touchcancel', () => { touching = false; queueSettle(); }, { passive: true });
    gallery.addEventListener('scroll', queueSettle, { passive: true });
    gallery.addEventListener('scrollend', settle);
    // Keep the same image in view when the viewport width changes.
    let lastIndex = 0;
    gallery.addEventListener('scroll', () => { lastIndex = current(); }, { passive: true });
    window.addEventListener('resize', () => jumpTo(lastIndex + offset));
  }

  // Dots and thumbnails jump to a photo; the active one follows the swipe.
  dots.forEach((dot) => {
    dot.addEventListener('click', () => goTo(Number(dot.dataset.galleryDot)));
  });
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryThumb)));
  });
  nav.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(current() - 1));
  nav.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(current() + 1));

  const markActive = () => {
    const index = current();
    [dots, thumbs].forEach((list) => {
      list.forEach((el, i) => {
        if (i === index) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
    });
    thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  let frame = 0;
  gallery.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(markActive);
  }, { passive: true });
}

const share = document.querySelector('[data-product-share]');

if (share) {
  share.addEventListener('click', async () => {
    const url = share.dataset.shareUrl;
    const title = share.dataset.shareTitle;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        // Dismissed share sheet: nothing to do.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      const label = share.querySelector('[data-share-label]');
      const original = label.textContent;
      label.textContent = share.dataset.copiedLabel;
      setTimeout(() => { label.textContent = original; }, 2000);
    } catch {
      window.prompt('', url);
    }
  });
}

// LISTEN: swap the cover for a YouTube player on click; tracklist rows load
// their video into the stage.
const listen = document.querySelector('[data-listen]');

if (listen) {
  const stage = listen.querySelector('[data-listen-stage]');
  if (stage) {
  const playButton = stage.querySelector('[data-listen-play]');
  const tracks = [...listen.querySelectorAll('[data-listen-track]')];

  const embed = (id) => {
    stage.querySelector('iframe')?.remove();
    const frame = document.createElement('iframe');
    frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
    frame.title = playButton.getAttribute('aria-label') || 'YouTube';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture';
    frame.allowFullscreen = true;
    frame.className = 'absolute inset-0 size-full';
    playButton.hidden = true;
    stage.append(frame);
  };

  playButton.addEventListener('click', () => embed(playButton.dataset.listenPlay));

  tracks.forEach((track) => {
    track.addEventListener('click', () => {
      tracks.forEach((t) => t.removeAttribute('aria-current'));
      track.setAttribute('aria-current', 'true');
      const id = track.dataset.listenTrack;
      playButton.dataset.listenPlay = id;
      playButton.querySelector('[data-listen-cover]').src = `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
      playButton.querySelector('[data-listen-title]').textContent = track.dataset.listenTrackTitle;
      playButton.querySelector('[data-listen-number]').textContent = track.dataset.listenTrackNumber;
      embed(id);
    });
  });
  }
}

// MP3 samples: one track plays at a time. On first play a DAW-style waveform
// is drawn under the row from the real audio (decoded once, min/max peaks per
// pixel column on a canvas); if the file can't be decoded (e.g. no CORS), a
// stable pseudo-waveform from the URL is used. The played part turns ink with
// a playhead line, the rest stays gray; clicking the waveform seeks.
const audioTracks = [...document.querySelectorAll('[data-audio-track]')];
const RESOLUTION = 2400;

const formatTime = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const pseudoPeaks = (seed) => {
  let h = 2166136261;
  for (const ch of seed) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const rand = () => {
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h >>> 0) % 10000) / 10000;
  };
  const min = new Float32Array(RESOLUTION);
  const max = new Float32Array(RESOLUTION);
  let env = 0.5;
  for (let i = 0; i < RESOLUTION; i++) {
    if (i % 24 === 0) env = 0.45 + rand() * 0.55;
    const beat = i % 48 < 6 ? 1 : 0.7;
    const swell = 0.6 + 0.4 * Math.sin((i / RESOLUTION) * Math.PI);
    const amp = Math.min(1, env * beat * swell * (0.55 + rand() * 0.45));
    max[i] = amp * (0.8 + rand() * 0.2);
    min[i] = -amp * (0.8 + rand() * 0.2);
  }
  return { min, max };
};

const realPeaks = async (url) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error('fetch failed');
  const buffer = await response.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const decoded = await ctx.decodeAudioData(buffer);
  ctx.close?.();
  const channels = [...Array(decoded.numberOfChannels)].map((_, c) => decoded.getChannelData(c));
  const length = channels[0].length;
  const block = Math.max(1, Math.floor(length / RESOLUTION));
  const step = Math.max(1, Math.floor(block / 256));
  const min = new Float32Array(RESOLUTION);
  const max = new Float32Array(RESOLUTION);
  let top = 0;
  for (let i = 0; i < RESOLUTION; i++) {
    let lo = 0;
    let hi = 0;
    const start = i * block;
    const end = Math.min(length, start + block);
    for (let j = start; j < end; j += step) {
      let v = 0;
      for (const data of channels) v += data[j];
      v /= channels.length;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    min[i] = lo;
    max[i] = hi;
    top = Math.max(top, hi, -lo);
  }
  top = top || 1;
  for (let i = 0; i < RESOLUTION; i++) {
    min[i] /= top;
    max[i] /= top;
  }
  return { min, max };
};

audioTracks.forEach((track) => {
  const audio = track.querySelector('audio');
  const toggle = track.querySelector('[data-audio-toggle]');
  const time = track.querySelector('[data-audio-time]');
  const wave = track.querySelector('[data-audio-wave]');
  const barsEl = track.querySelector('[data-audio-bars]');
  const canvas = document.createElement('canvas');
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.setAttribute('aria-hidden', 'true');
  barsEl.replaceChildren(canvas);
  let peaks = null;
  let built = false;
  let frame = 0;

  const paint = () => {
    if (!peaks) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = barsEl.clientWidth;
    const cssH = barsEl.clientHeight;
    if (!cssW || !cssH) return;
    const w = Math.round(cssW * dpr);
    const h = Math.round(cssH * dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const styles = getComputedStyle(barsEl);
    const ink = styles.getPropertyValue('--color-on-surface').trim() || '#171717';
    const isRtl = styles.direction === 'rtl';
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    const mid = h / 2;
    const half = h / 2 - dpr;
    const ratio = audio.duration ? audio.currentTime / audio.duration : 0;
    const playedX = Math.round(ratio * w);

    // Center line.
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = ink;
    ctx.fillRect(0, Math.round(mid), w, Math.max(1, Math.round(dpr * 0.5)));

    for (let x = 0; x < w; x++) {
      const from = Math.floor((x / w) * RESOLUTION);
      const to = Math.max(from + 1, Math.floor(((x + 1) / w) * RESOLUTION));
      let lo = 0;
      let hi = 0;
      for (let i = from; i < to && i < RESOLUTION; i++) {
        if (peaks.min[i] < lo) lo = peaks.min[i];
        if (peaks.max[i] > hi) hi = peaks.max[i];
      }
      const y1 = mid - hi * half;
      const y2 = mid - lo * half;
      const px = isRtl ? w - 1 - x : x;
      ctx.globalAlpha = x < playedX ? 1 : 0.28;
      ctx.fillRect(px, y1, 1, Math.max(1, y2 - y1));
    }

    // Playhead.
    if (ratio > 0) {
      ctx.globalAlpha = 1;
      const px = isRtl ? w - playedX : playedX;
      ctx.fillRect(Math.min(w - dpr, px), 0, Math.max(1, Math.round(dpr)), h);
    }
    ctx.globalAlpha = 1;
  };

  const loop = () => {
    paint();
    if (!audio.paused) frame = requestAnimationFrame(loop);
  };

  new ResizeObserver(() => paint()).observe(barsEl);

  const build = async () => {
    if (built) return;
    built = true;
    peaks = pseudoPeaks(audio.currentSrc || audio.src);
    paint();
    try {
      peaks = await realPeaks(audio.currentSrc || audio.src);
      paint();
    } catch {
      // Keep the pseudo-waveform.
    }
  };

  toggle.addEventListener('click', () => {
    if (audio.paused) {
      audioTracks.forEach((other) => {
        if (other === track) return;
        other.querySelector('audio').pause();
        other.querySelector('[data-audio-wave]').removeAttribute('data-open');
      });
      wave.setAttribute('data-open', '');
      build();
      audio.play();
    } else {
      audio.pause();
    }
  });

  audio.addEventListener('play', () => {
    toggle.setAttribute('aria-pressed', 'true');
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(loop);
  });
  audio.addEventListener('pause', () => {
    toggle.setAttribute('aria-pressed', 'false');
    paint();
  });
  audio.addEventListener('ended', () => toggle.setAttribute('aria-pressed', 'false'));
  audio.addEventListener('timeupdate', () => {
    time.textContent = formatTime(audio.currentTime);
    if (audio.paused) paint();
  });

  barsEl.addEventListener('click', (event) => {
    if (!audio.duration) return;
    const rect = barsEl.getBoundingClientRect();
    const isRtl = getComputedStyle(barsEl).direction === 'rtl';
    const offset = isRtl ? rect.right - event.clientX : event.clientX - rect.left;
    audio.currentTime = Math.min(1, Math.max(0, offset / rect.width)) * audio.duration;
    paint();
  });
});
